import {
  AgendamentoCuidadoCompartilhadoInput,
  InterconsultaAgendamentoCuidadoCompartilhadoInput,
  SalvarCuidadoCompartilhadoInput,
} from 'graphql/types.generated'
import { ErrorObject } from 'util/validation'
import {
  AgendarConsultaCuidadoCompartilhadoFieldsModel,
  CondutaGroupEnum,
  CuidadoCompartilhadoModel,
  DiscussaoCasoExecutanteFieldsModel,
  DiscussaoCasoFormModel,
  DiscussaoCasoSolicitanteFieldsModel,
  TipoAgendamentoCuidadoCompartilhado,
  TrocaExecutanteCuidadoCompartilhadoFieldsModel,
} from 'view/cuidados-compartilhados/model-cuidadoCompartilhado'
import {
  getResponsabilidadeCuidadoCompartilhado,
  isReclassificandoCuidadoCompartilhado,
} from 'view/cuidados-compartilhados/util-cuidadoCompartilhado'

const convertAgendamentoFormToInput = (
  values: AgendarConsultaCuidadoCompartilhadoFieldsModel['consultaComCidadao'],
  showHorarioFinal: boolean
): AgendamentoCuidadoCompartilhadoInput => ({
  horarioInicial: Number(values.horario.inicial),
  horarioFinal: showHorarioFinal && values.horario?.final ? Number(values.horario.final) : undefined,
  observacoes: values.observacoes,
})

const convertConsultaCompartilhadaFormToInput = (
  values: AgendarConsultaCuidadoCompartilhadoFieldsModel['consultaCompartilhada'],
  videochamadaUuid: string
): InterconsultaAgendamentoCuidadoCompartilhadoInput => {
  return {
    horarioFinal: Number(values.horario.final),
    horarioInicial: Number(values.horario.inicial),
    observacoes: values.observacoes,
    cidadaoPresente: values.isCidadaoPresente,
    videochamadaUuid,
  }
}

const convertAgendarConsultaFormToInput = (
  values: DiscussaoCasoExecutanteFieldsModel | DiscussaoCasoSolicitanteFieldsModel,
  videochamadaUuid: string,
  showHorarioFinal: boolean
): Partial<SalvarCuidadoCompartilhadoInput> => {
  if (
    values[CondutaGroupEnum.AGENDAR_CONSULTA].tipoAgendamento ===
    TipoAgendamentoCuidadoCompartilhado.CONSULTA_COM_CIDADAO
  ) {
    return {
      agendamento: convertAgendamentoFormToInput(
        values[CondutaGroupEnum.AGENDAR_CONSULTA].consultaComCidadao,
        showHorarioFinal
      ),
    }
  } else {
    return {
      interconsulta: convertConsultaCompartilhadaFormToInput(
        values[CondutaGroupEnum.AGENDAR_CONSULTA].consultaCompartilhada,
        videochamadaUuid
      ),
    }
  }
}

const convertExecutanteFieldsFormToInput = (
  values: DiscussaoCasoExecutanteFieldsModel,
  videochamadaUuid: string,
  showHorarioFinal: boolean
): Omit<SalvarCuidadoCompartilhadoInput, 'cuidadoCompartilhadoId'> => {
  let fieldInput = {}

  switch (values?.condutaGroup) {
    case CondutaGroupEnum.DEVOLUTIVA_DISCUSSAO_CASO: {
      fieldInput['devolutiva'] = { ...values[CondutaGroupEnum.DEVOLUTIVA_DISCUSSAO_CASO] }
      break
    }
    case CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA: {
      fieldInput['trocaExecutante'] = {
        lotacaoExecutanteId: values[CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA].lotacao.id,
        motivo: values[CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA].motivo,
      }
      break
    }
    case CondutaGroupEnum.AGENDAR_CONSULTA: {
      fieldInput = convertAgendarConsultaFormToInput(values, videochamadaUuid, showHorarioFinal)
      break
    }
    case CondutaGroupEnum.SUGESTAO_AGENDAMENTO_GRUPO: {
      fieldInput['sugestaoAgendamentoGrupo'] = { ...values[CondutaGroupEnum.SUGESTAO_AGENDAMENTO_GRUPO] }
      break
    }
    case CondutaGroupEnum.ENVIAR_GARANTIA_ACESSO: {
      fieldInput['enviarGarantiaAcesso'] = {
        tipoAgendamento: values[CondutaGroupEnum.ENVIAR_GARANTIA_ACESSO].tipoAgendamento,
        motivo: values[CondutaGroupEnum.ENVIAR_GARANTIA_ACESSO].motivo,
      }
      break
    }
    default: {
      break
    }
  }

  return fieldInput
}

const convertSolicitanteFieldsFormToInput = (
  values: DiscussaoCasoSolicitanteFieldsModel,
  videochamadaUuid: string,
  showHorarioFinal: boolean
): Omit<SalvarCuidadoCompartilhadoInput, 'cuidadoCompartilhadoId'> => {
  let fieldInput = {}

  switch (values?.condutaGroup) {
    case CondutaGroupEnum.NOVA_PERGUNTA: {
      fieldInput['pergunta'] = { ...values[CondutaGroupEnum.NOVA_PERGUNTA] }
      break
    }
    case CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA: {
      fieldInput['trocaExecutante'] = {
        lotacaoExecutanteId: values[CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA].lotacao.id,
        motivo: values[CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA].motivo,
      }
      break
    }
    case CondutaGroupEnum.AGENDAR_CONSULTA: {
      fieldInput = convertAgendarConsultaFormToInput(values, videochamadaUuid, showHorarioFinal)
      break
    }
    default: {
      break
    }
  }

  return fieldInput
}

export const convertDiscussaoCasoFormModelToSalvarCuidadoCompartilhadoInput = (
  values: DiscussaoCasoFormModel,
  cuidadoCompartilhado: CuidadoCompartilhadoModel,
  lotacaoId: ID,
  videochamadaUuid: string,
  showHorarioFinal: boolean
): SalvarCuidadoCompartilhadoInput => {
  const responsabilidade = getResponsabilidadeCuidadoCompartilhado(
    lotacaoId,
    cuidadoCompartilhado.lotacaoExecutanteAtual.id,
    cuidadoCompartilhado.lotacaoSolicitante.id
  )

  const isReclassificando = isReclassificandoCuidadoCompartilhado(
    cuidadoCompartilhado.classificacaoPrioridadeAtual,
    values.reclassificaoPrioridade
  )

  return {
    cuidadoCompartilhadoId: cuidadoCompartilhado.id,
    reclassificacaoPrioridade: isReclassificando ? values.reclassificaoPrioridade : null,
    ...(responsabilidade === 'EXECUTANTE'
      ? convertExecutanteFieldsFormToInput(values.executanteFields, videochamadaUuid, showHorarioFinal)
      : convertSolicitanteFieldsFormToInput(values.solicitanteFields, videochamadaUuid, showHorarioFinal)),
  }
}

const convertConsultaComCidadaoBackendErrorsToForm = (
  input: SalvarCuidadoCompartilhadoInput['agendamento']
): ErrorObject<AgendarConsultaCuidadoCompartilhadoFieldsModel['consultaComCidadao']> => ({
  horario: {
    inicial: input?.horarioInicial,
    final: input?.horarioFinal,
  },
})

const convertConsultaCompartilhadaBackendErrorsToForm = (
  input: SalvarCuidadoCompartilhadoInput['interconsulta']
): string | ErrorObject<AgendarConsultaCuidadoCompartilhadoFieldsModel['consultaCompartilhada']> => {
  if (input?.videochamadaUuid) {
    return input?.videochamadaUuid
  }

  const hasErrorHorario = input?.horarioInicial || input?.horarioFinal

  if (hasErrorHorario) {
    return {
      horario: {
        inicial: input?.horarioInicial,
        final: input?.horarioFinal,
      },
    }
  }
}

const convertTrocaExecutanteBackendErrorsToForm = (
  input: SalvarCuidadoCompartilhadoInput['trocaExecutante']
): ErrorObject<TrocaExecutanteCuidadoCompartilhadoFieldsModel> => ({
  lotacao: input?.lotacaoExecutanteId,
  motivo: input?.motivo,
})

export const convertDiscussaoCasoBackendErrorsToForm = (
  validationErrors: ErrorObject<SalvarCuidadoCompartilhadoInput>
): ErrorObject<DiscussaoCasoFormModel> => {
  const input = validationErrors as SalvarCuidadoCompartilhadoInput

  return {
    executanteFields: {
      [CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA]: convertTrocaExecutanteBackendErrorsToForm(
        input?.trocaExecutante
      ),
      [CondutaGroupEnum.AGENDAR_CONSULTA]: {
        consultaComCidadao: convertConsultaComCidadaoBackendErrorsToForm(input?.agendamento),
        consultaCompartilhada: convertConsultaCompartilhadaBackendErrorsToForm(input?.interconsulta),
      },
    },
    solicitanteFields: {
      [CondutaGroupEnum.TROCA_PROFISSIONAL_REFERENCIA]: convertTrocaExecutanteBackendErrorsToForm(
        input?.trocaExecutante
      ),
      [CondutaGroupEnum.AGENDAR_CONSULTA]: {
        consultaComCidadao: convertConsultaComCidadaoBackendErrorsToForm(input?.agendamento),
        consultaCompartilhada: convertConsultaCompartilhadaBackendErrorsToForm(input?.interconsulta),
      },
    },
  }
}
